import { TaxonomyFacet, TaxonomyTermStatus } from '@edgebox/sync-core-data-definitions';
import type { IInventoryFilterValue } from './Onboarding/ContentInventory/Components/AddFilterDropdown';

export const QUERY_TERMS_PARAM = 'terms';
import { parseVisibilityDisplayMode, VisibilityDisplayMode } from './Components/visibilityGranularity.utils';
import { DEFAULT_SOURCE_GROUPING, SourceGrouping } from './Components/citationSources.utils';
import { classifyEntityOwnership, EntityOwnership } from './GovernanceAnalyticsAiAnswers.utils';
import { buildEntityColorMap } from './Components/entityPalette';
import { colorPerceptuallyClose, HEX_COLOR_PATTERN } from './Components/governanceThemeColors';
import { IToneBearingRow, TONE_METRIC_LABEL, ToneMetricName, toneValue } from './toneMetric';

/**
 * One taxonomy's terms, as the AI Visibility page's own facet-scoped fetches load them
 * (`getCachedTaxonomyTermsByFacet`, one entry per facet that has a taxonomy and at least one term).
 *
 * Shared rather than declared per-module: the Attributes module reads Competitor and
 * CompetitorProduct from the SAME lists the page's Breakdown/Gaps facets already hold, instead of
 * fetching them a second time through the term index — see `findConfiguredTerm` below.
 *
 * Generic over which facets it carries, defaulting to the full enum: a consumer that only reads a
 * term by key (Attributes, `AttributeComparisonPicker`) takes the default, while the page that
 * builds the list keeps its own narrower facet union (`AnalyticsFacet`) so its existing
 * facet-membership checks stay exactly as precise as before this type moved here.
 */
export type FacetConfig<TFacet extends TaxonomyFacet = TaxonomyFacet> = {
  facet: TFacet;
  taxonomyKey: string;
  taxonomyName: string;
  terms: { id: string; key: string; name: string; status?: TaxonomyTermStatus; color?: string }[];
};

/**
 * The stored brand colours a facet-config list carries, keyed by both a term's key and its id so a
 * lookup resolves whichever the caller holds. The visibility screen reads its terms from these
 * per-facet lists rather than the shared index, so its stored colours ride the same data.
 */
export function storedColorsFromFacetConfigs(configs: readonly FacetConfig[]): ReadonlyMap<string, string> {
  const colors = new Map<string, string>();
  for (const config of configs) {
    for (const term of config.terms) {
      // Normalised then validated from the one shared pattern, the same boundary the term index
      // applies, so a malformed value never reaches a painter and the two paths agree on any case.
      const value = (term.color || '').trim().toLowerCase();
      if (!HEX_COLOR_PATTERN.test(value)) {
        continue;
      }
      if (term.key) {
        colors.set(term.key, value);
      }
      if (term.id) {
        colors.set(String(term.id), value);
      }
    }
  }
  return colors;
}

/** Finds a term by key, id, or name across every configured facet. */
export function findConfiguredTerm(configs: FacetConfig[], value: string) {
  for (const config of configs) {
    const matched = config.terms.find((term) => term.key === value || term.id === value || term.name === value);
    if (matched) {
      return matched;
    }
  }
  return undefined;
}

/**
 * Whether a stats row's term is one this screen can name.
 *
 * The facet term lists exclude archived terms, and the stats endpoints deliberately do NOT — the
 * server keeps archived aggregation rows so un-archiving a term surfaces its history at once. So a
 * row keyed to an archived (or deleted) term arrives here with nothing to name it, and
 * `resolveTermName` would fall back to printing the raw key. Such rows are dropped instead: an
 * opaque key is no more meaningful to a reader than a blank.
 */
export function isDisplayableStatsTerm(configs: FacetConfig[], value: string | null): boolean {
  // The explicit "no term assigned" bucket is a real, nameable category, not an unresolved term.
  if (value === null) {
    return true;
  }
  const matched = findConfiguredTerm(configs, value);
  if (!matched) {
    return false;
  }
  return !!matched;
}

export function resolveTermName(configs: FacetConfig[], value: string): string {
  return findConfiguredTerm(configs, value)?.name ?? value;
}

export const QUERY_PURPOSE_PARAM = 'purpose';
/**
 * The Engine, Locale and Prompt filters. Present only while one narrows the screen — and under the
 * same parameter names the answers list uses, so the reading survives the drill-down both ways.
 */
export const QUERY_ENGINE_PARAM = 'engine';
export const QUERY_LOCALE_PARAM = 'locale';
export const QUERY_PROMPT_PARAM = 'prompt';
/** Trend bucket granularity. Present only when the reader chose one, so a default can fill the gap. */
export const QUERY_GRANULARITY_PARAM = 'granularity';
/** Whether unapproved suggestions are held out of the analytics. Present only when turned on. */
export const QUERY_HIDE_SUGGESTIONS_PARAM = 'hideSuggestions';
/** Which view the Attributes section opens in. Present only when it is not the default. */
export const QUERY_ATTRIBUTE_VIEW_PARAM = 'attributeView';
/**
 * How the Sources section groups cited websites. Present only when it is not the default.
 *
 * Deliberately the same parameter name the answers list uses, so the reading the reader chose
 * survives the drill-down in both directions instead of resetting on arrival.
 */
export const QUERY_SOURCE_GROUPING_PARAM = 'sourceGrouping';

/** The Attributes section's two readings: the shape, or the numbers. */
export type AttributeView = 'radar' | 'table';

/** Parses the view, defaulting to the shape — the reading the section leads with. */
export function parseAttributeView(value: string | undefined): AttributeView {
  return value === 'table' ? 'table' : 'radar';
}

/**
 * The AI Visibility KPIs. `recommendation` is the Attribution v2 KPI ("share of answers in
 * which this brand is the explicitly declared winner"); which of these a purpose shows is
 * decided by its `PurposeKpiMask` (`Components/purposeKpiMask.utils.ts`), not by this type.
 */
export type AiKpi = 'visibility' | ToneMetricName | 'shareOfVoice' | 'recommendation';

/**
 * Whether a KPI key names the tone measure, whichever of the two `TONE_METRIC` currently selects.
 *
 * Both spellings answer true, because a key outlives the switch: a bookmarked URL, a stored table
 * preference and a link someone pasted into a chat all carry the spelling that was current when
 * they were written, and a reader following one of those is asking for the tone column either way.
 */
export function isToneKpi(metric: string): boolean {
  return metric === 'sentiment' || metric === 'verdict';
}

export function metricLabel(metric: AiKpi): string {
  if (metric === 'visibility') {
    return 'Visibility';
  }
  if (isToneKpi(metric)) {
    return TONE_METRIC_LABEL;
  }
  if (metric === 'recommendation') {
    return 'Recommendation';
  }

  return 'Share of Voice';
}

export function metricValue(
  entry: {
    visibility?: number;
    shareOfVoice?: number;
    recommendation?: number;
  } & IToneBearingRow,
  metric: AiKpi
): number | undefined {
  if (metric === 'visibility') {
    return entry.visibility;
  }
  if (isToneKpi(metric)) {
    return toneValue(entry, 'own');
  }
  if (metric === 'recommendation') {
    return entry.recommendation;
  }

  return entry.shareOfVoice;
}

/**
 * The analyzed prompt runs a `/total` response reports — the figure behind "AI answers analyzed".
 *
 * It is `promptRunCount`, never `count`. One prompt run is one answer: one prompt asked once of one
 * engine. `count` (and its clearer alias `promptCount`) instead aggregates one document per
 * monitoring PROMPT, so a project asking fifty prompts of four engines a day reported fifty where
 * the answers numbered thousands — a prompt count printed under an answers label, and the same
 * mistaken denominator that had the Sources rows saying "cited in 450 answers of 290".
 *
 * Guarded rather than read straight off the field, even though the generated client declares it
 * required: a Sync Core older than the release that added it answers without one, and a generated
 * client maps a field it never received to undefined. Reporting that absence AS absence — instead of
 * the 0 that would claim nothing had been analyzed — is what lets the card show a dash.
 */
export function analyzedPromptRunCountOf(total: {
  /** Distinct prompts. Named here so the field this function must NOT return is visible in its type. */
  count?: number | null;
  /** `count` under a name that states its unit. Same value, same reason to leave it alone. */
  promptCount?: number | null;
  promptRunCount?: number | null;
}): number | undefined {
  const analyzed = total?.promptRunCount;
  return typeof analyzed === 'number' && Number.isFinite(analyzed) && analyzed >= 0 ? analyzed : undefined;
}

export type FiltersPayload = {
  purposeTermKey: string;
  taxonomyTerms?: string[];
  engineKey?: string;
  localeKey?: string;
  promptKey?: string;
};

export type IAnalyticsQuery = {
  selectedFilters: IInventoryFilterValue[];
  purposeTermKey: string;
  /** The Engine, Locale and Prompt filters; empty where nothing narrows the screen. */
  engineKey: string;
  localeKey: string;
  promptKey: string;
  /** Present only when the reader explicitly chose a granularity; otherwise the default applies. */
  displayMode?: VisibilityDisplayMode;
  /** Whether suggested terms are held out of every figure on the screen. */
  hideSuggestions: boolean;
  /** Which reading the Attributes section opens in. */
  attributeView: AttributeView;
  /** How the Sources section groups cited websites. */
  sourceGrouping: SourceGrouping;
};

export function buildStatsFilters(
  selectedFilters: IInventoryFilterValue[],
  engineKey: string,
  localeKey: string,
  promptKey: string,
  purposeTermKey: string
): FiltersPayload {
  const taxonomyTerms = selectedFilters
    .filter((filter) => filter.targetType === 'taxonomy')
    .map((filter) => filter.valueId)
    .filter((term) => term.includes('.'));

  return {
    purposeTermKey,
    taxonomyTerms: taxonomyTerms.length ? taxonomyTerms : undefined,
    engineKey: engineKey || undefined,
    localeKey: localeKey || undefined,
    promptKey: promptKey || undefined,
  };
}

export function serializeFiltersParam(filters: IInventoryFilterValue[]): string {
  return JSON.stringify(
    filters.map((filter) => ({
      targetId: filter.targetId,
      targetLabel: filter.targetLabel,
      targetType: filter.targetType,
      valueId: filter.valueId,
      valueLabel: filter.valueLabel,
    }))
  );
}

function coerceQueryStringValue(value: unknown): string | undefined {
  if (typeof value === 'string') {
    return value;
  }

  if (Array.isArray(value)) {
    const firstString = value.find((entry) => typeof entry === 'string');
    return typeof firstString === 'string' ? firstString : undefined;
  }

  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }

  return undefined;
}

function parseCsvValue(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value
      .flatMap((entry) => parseCsvValue(entry))
      .map((entry) => entry.trim())
      .filter(Boolean);
  }

  const serialized = coerceQueryStringValue(value);
  if (!serialized) {
    return [];
  }

  return serialized
    .split(',')
    .map((entry) => entry.trim())
    .filter(Boolean);
}

function toCsvValue(values: string[]): string | undefined {
  const deduped = Array.from(new Set(values.map((value) => value.trim()).filter(Boolean)));
  return deduped.length ? deduped.join(',') : undefined;
}

function readQueryParamFromSource(source: unknown, key: string): unknown {
  if (!source) {
    return undefined;
  }

  if (source instanceof URLSearchParams) {
    const values = source.getAll(key).filter((entry) => entry.trim().length > 0);
    if (!values.length) {
      return undefined;
    }

    return values.length === 1 ? values[0] : values;
  }

  if (typeof source === 'string') {
    const params = new URLSearchParams(source.replace(/^\?/, ''));
    const values = params.getAll(key).filter((entry) => entry.trim().length > 0);
    if (!values.length) {
      return undefined;
    }

    return values.length === 1 ? values[0] : values;
  }

  if (Array.isArray(source)) {
    for (const entry of source) {
      const value = readQueryParamFromSource(entry, key);
      if (value !== undefined) {
        return value;
      }
    }

    return undefined;
  }

  if (typeof source === 'object') {
    const record = source as Record<string, unknown>;
    const direct = record[key];
    if (direct !== undefined && direct !== null && !(typeof direct === 'string' && !direct.trim())) {
      return direct;
    }

    if (record.query !== undefined) {
      const nested = readQueryParamFromSource(record.query, key);
      if (nested !== undefined) {
        return nested;
      }
    }
  }

  return undefined;
}

function termLabelFromKey(value: string): string {
  const term = value.split('.').pop() || value;
  return term.replace(/[-_]+/g, ' ');
}

function buildFilterValuesFromQuery(taxonomyTermValues: string[]): IInventoryFilterValue[] {
  return taxonomyTermValues.map((value) => ({
    targetId: 'taxonomy',
    targetLabel: 'Taxonomy',
    targetType: 'taxonomy',
    valueId: value,
    valueLabel: termLabelFromKey(value),
  }));
}

/**
 * The filters as the host page's address carries them. Only that address is read: the frame's own
 * `location.search` is what a link put there once and nothing rewrites, so a filter removed from the
 * host page's address would come back from that stale copy on the next parse.
 */
export function parseAnalyticsQuery(query: unknown, brandPresencePath?: string): IAnalyticsQuery {
  const pathParams = new URLSearchParams(brandPresencePath?.split('?')[1] || '');

  const readParam = (key: string): unknown => {
    const fromQuery = readQueryParamFromSource(query, key);
    if (fromQuery !== undefined) {
      return fromQuery;
    }

    const fromPath = pathParams.get(key);
    if (fromPath !== null && fromPath.trim()) {
      return fromPath;
    }

    return undefined;
  };

  const termsRaw = readParam(QUERY_TERMS_PARAM);
  const taxonomyTermValues = parseCsvValue(termsRaw);

  const purposeRaw = coerceQueryStringValue(readParam(QUERY_PURPOSE_PARAM));
  const engineRaw = coerceQueryStringValue(readParam(QUERY_ENGINE_PARAM));
  const localeRaw = coerceQueryStringValue(readParam(QUERY_LOCALE_PARAM));
  const promptRaw = coerceQueryStringValue(readParam(QUERY_PROMPT_PARAM));
  const granularityRaw = coerceQueryStringValue(readParam(QUERY_GRANULARITY_PARAM));
  const hideSuggestionsRaw = coerceQueryStringValue(readParam(QUERY_HIDE_SUGGESTIONS_PARAM));
  const attributeViewRaw = coerceQueryStringValue(readParam(QUERY_ATTRIBUTE_VIEW_PARAM));
  const sourceGroupingRaw = coerceQueryStringValue(readParam(QUERY_SOURCE_GROUPING_PARAM));

  return {
    selectedFilters: taxonomyTermValues.length ? buildFilterValuesFromQuery(taxonomyTermValues) : [],
    purposeTermKey: purposeRaw?.trim() || '',
    engineKey: engineRaw?.trim() || '',
    localeKey: localeRaw?.trim() || '',
    promptKey: promptRaw?.trim() || '',
    // Left undefined rather than defaulted here: the age-derived default needs the project, which
    // this parser has no business knowing about.
    displayMode: parseVisibilityDisplayMode(granularityRaw),
    // Off unless explicitly asked for. Suggestions are the screen's own findings about terms the
    // customer has not yet ruled on, so hiding them by default would bury the work the product did.
    hideSuggestions: hideSuggestionsRaw === '1' || hideSuggestionsRaw === 'true',
    attributeView: parseAttributeView(attributeViewRaw),
    // Anything unrecognised means the default reading, so a mangled link still opens a working view.
    sourceGrouping: sourceGroupingRaw === 'host' ? 'host' : DEFAULT_SOURCE_GROUPING,
  };
}

export function buildUrlQueryFromState(params: {
  selectedFilters: IInventoryFilterValue[];
  purposeTermKey: string;
  engineKey?: string;
  localeKey?: string;
  promptKey?: string;
  displayMode?: VisibilityDisplayMode;
  hideSuggestions?: boolean;
  attributeView?: AttributeView;
  sourceGrouping?: SourceGrouping;
}): Record<string, string> {
  const taxonomyTermValues = params.selectedFilters
    .filter((filter) => filter.targetType === 'taxonomy')
    .map((filter) => filter.valueId)
    .filter((value) => value.includes('.'));

  return {
    ...(toCsvValue(taxonomyTermValues) ? { [QUERY_TERMS_PARAM]: toCsvValue(taxonomyTermValues) as string } : {}),
    ...(params.purposeTermKey ? { [QUERY_PURPOSE_PARAM]: params.purposeTermKey } : {}),
    ...(params.engineKey ? { [QUERY_ENGINE_PARAM]: params.engineKey } : {}),
    ...(params.localeKey ? { [QUERY_LOCALE_PARAM]: params.localeKey } : {}),
    ...(params.promptKey ? { [QUERY_PROMPT_PARAM]: params.promptKey } : {}),
    ...(params.displayMode ? { [QUERY_GRANULARITY_PARAM]: params.displayMode } : {}),
    // Only serialised when on, so the common case leaves no parameter behind.
    ...(params.hideSuggestions ? { [QUERY_HIDE_SUGGESTIONS_PARAM]: '1' } : {}),
    // Only when it differs from the default, so a shared link stays clean.
    ...(params.attributeView === 'table' ? { [QUERY_ATTRIBUTE_VIEW_PARAM]: 'table' } : {}),
    ...(params.sourceGrouping === 'host' ? { [QUERY_SOURCE_GROUPING_PARAM]: 'host' } : {}),
  };
}

/** Whether a metric was actually measured, as opposed to absent. */
export function isMeasuredMetric(value: number | null | undefined): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

/**
 * The mean of one metric across the rows a reader can SEE, or undefined when none were measured.
 *
 * The gaps reference marker used to come from the KPI totals, which are a different query — the
 * gaps fetch adds a facet filter the summary fetch does not — so the line a reader compared each
 * bar against was drawn from a population that is not the rows in front of them.
 *
 * Unmeasured rows are excluded from the denominator rather than counted as zero: a term nobody
 * measured must not drag the average of the terms somebody did.
 */
export function averageOfMeasured<T>(rows: readonly T[], pick: (row: T) => number | null | undefined): number | undefined {
  const measured = rows.map(pick).filter(isMeasuredMetric);
  return measured.length ? measured.reduce((total, value) => total + value, 0) / measured.length : undefined;
}

/**
 * The largest measured value among the rows given, or undefined when none were measured.
 *
 * Companion to `averageOfMeasured`, and it must be fed the SAME rows: a relative axis scaled to a
 * maximum drawn from a different population would make two rows of equal value look different.
 */
export function maximumOfMeasured<T>(rows: readonly T[], pick: (row: T) => number | null | undefined): number | undefined {
  const measured = rows.map(pick).filter(isMeasuredMetric);
  return measured.length ? Math.max(...measured) : undefined;
}

/**
 * Answers behind a mean position before its badge reads as settled rather than provisional.
 *
 * A rank computed from one or two answers moves the moment a third arrives, so the badge says so
 * instead of presenting it with the same authority as a rank drawn from forty. Below the floor the
 * rank is still SHOWN with its n — suppressing it would hide that the subject was positioned at
 * all, which is the opposite of what a reader needs.
 */
export const MIN_POSITION_RUNS_FOR_SETTLED_RANK = 3;

/**
 * A stats row's position figures, carried across unchanged when they are real.
 *
 * SEPARATE FROM `normalizeScore` ON PURPOSE. Every other metric on this row is a 0-1 score, and
 * that helper clamps to 0-1 accordingly. Position is neither bounded nor a score: clamping a mean
 * of 1.38 to 1 would tie every subject at the same rank, and clamping an n of 41 to 1 would make
 * every rank look provisional. This reads the two fields with a finiteness guard and nothing else.
 */
export function subjectPositionOf(entry: { avgPosition?: number | null; positionRunCount?: number | null }): {
  avgPosition?: number;
  positionRunCount?: number;
} {
  return {
    avgPosition: isMeasuredMetric(entry.avgPosition) ? entry.avgPosition : undefined,
    positionRunCount: isMeasuredMetric(entry.positionRunCount) ? entry.positionRunCount : undefined,
  };
}

/** A subject competing for a position rank. */
export interface PositionRankSubject {
  /** The subject's term key. `null` is the "no term assigned" bucket: untracked, never ranked. */
  term: string | null;
  /** Mean of the subject's position across the answers that positioned it. */
  avgPosition?: number | null;
  /** How many answers that mean is drawn from. */
  positionRunCount?: number | null;
  /** Tie-breaker only — never itself a rank input. */
  shareOfVoice?: number | null;
}

/** What the badge renders for one subject. */
export interface PositionRank {
  /** 1-based ordinal. Absent when the subject was never positioned. */
  rank?: number;
  /** The mean the rank was derived from, for the hover. */
  avgPosition?: number;
  /** The n behind the mean, for the hover and for the provisional reading. */
  positionRunCount?: number;
  /** False while fewer than {@link MIN_POSITION_RUNS_FOR_SETTLED_RANK} answers back the mean. */
  settled: boolean;
}

/**
 * Rank every subject by its mean position, best (lowest) first.
 *
 * TRACKED SUBJECTS ONLY. The "no term assigned" bucket is an aggregate of everything untracked, so
 * a rank for it would compare a crowd against individuals. It is returned unranked rather than
 * omitted, because the row still exists in the table.
 *
 * A subject with no mean is NOT ranked last — it is not ranked at all. Never having been positioned
 * is the absence of a measurement, and the badge says that; ranking it behind the measured subjects
 * would invent an ordering the data does not carry, and `#0` would invent a value.
 *
 * TIES SHARE A RANK. Two subjects averaging 1.5 are both `#1` and the next is `#3`, the standard
 * competition ranking — nudging one ahead of the other would report a difference that was not
 * measured. Share of voice orders tied subjects for display only, so the list is stable rather than
 * dependent on the order the rows arrived in; it never changes the rank itself.
 */
export function rankSubjectsByPosition(subjects: readonly PositionRankSubject[]): Map<string, PositionRank> {
  const ranked = subjects
    .flatMap((subject) => {
      // Destructured so the measured-metric guard narrows the values themselves — a guard applied
      // to `subject.avgPosition` inside a filter would leave the property optional and force a cast.
      const { term, avgPosition, positionRunCount, shareOfVoice } = subject;
      if (typeof term !== 'string' || !isMeasuredMetric(avgPosition)) {
        return [];
      }
      return [
        {
          term,
          avgPosition,
          positionRunCount: isMeasuredMetric(positionRunCount) ? positionRunCount : undefined,
          shareOfVoice: isMeasuredMetric(shareOfVoice) ? shareOfVoice : undefined,
        },
      ];
    })
    .sort((left, right) => {
      if (left.avgPosition !== right.avgPosition) {
        return left.avgPosition - right.avgPosition;
      }
      // Display order within a tie: more share of voice first, then by term so the order cannot
      // depend on how the rows happened to arrive.
      const shareDifference = (right.shareOfVoice ?? -1) - (left.shareOfVoice ?? -1);
      return shareDifference !== 0 ? shareDifference : left.term.localeCompare(right.term);
    });

  const byTerm = new Map<string, PositionRank>();
  let rank = 0;
  let previousPosition: number | undefined;
  ranked.forEach((subject, index) => {
    // Competition ranking: equal means share the ordinal, and the ordinal after a tie skips.
    if (subject.avgPosition !== previousPosition) {
      rank = index + 1;
      previousPosition = subject.avgPosition;
    }
    byTerm.set(subject.term, {
      rank,
      avgPosition: subject.avgPosition,
      positionRunCount: subject.positionRunCount,
      settled: (subject.positionRunCount ?? 0) >= MIN_POSITION_RUNS_FOR_SETTLED_RANK,
    });
  });

  return byTerm;
}

/**
 * The hover text behind a position badge: the mean the rank came from and the answers behind it.
 *
 * The mean is shown to two decimals because that is the precision that distinguishes adjacent
 * subjects; rounding it to the ordinal would hide why two subjects rank differently.
 */
export function positionHoverLabel(rank: PositionRank): string {
  const average = `avg ${rank.avgPosition?.toFixed(2) ?? '-'}`;
  const runs = rank.positionRunCount === undefined ? '' : ` · n=${rank.positionRunCount}`;
  const provisional = rank.settled ? '' : ` · provisional, fewer than ${MIN_POSITION_RUNS_FOR_SETTLED_RANK} answers`;
  return `Mean position ${average}${runs}${provisional}`;
}

/** One Breakdown series before it has been ordered or coloured. */
export interface BreakdownSeriesSubject {
  id: string;
  label: string;
  /** The facet the term belongs to. Absent for the "no term assigned" series. */
  facet?: TaxonomyFacet;
}

export interface ColoredBreakdownSeries extends BreakdownSeriesSubject {
  color: string;
  ownership: EntityOwnership;
}

/** Own entities lead, then rivals, then anything belonging to neither. */
const OWNERSHIP_ORDER: Readonly<Record<EntityOwnership, number>> = { own: 0, competitor: 1, other: 2 };

/**
 * Orders the Breakdown's series and gives each one its colour.
 *
 * Ownership decides both, through the same helpers the answers list and answer detail use:
 * `classifyEntityOwnership` for the ordering and `buildEntityColorMap` for the assignment. An
 * own-side series takes the theme primary, and a rival cannot be handed it while an own series is
 * present. Rivals are assigned from the shared comparison palette BY NAME, so the same rival keeps
 * one colour across all three surfaces — a brand cannot render as one colour here and another on
 * the answers list.
 *
 * A series belonging to neither side — the "no term assigned" bucket — takes the next unused
 * comparison colour rather than none, since it still has to be told apart on the chart.
 */
export function orderAndColorBreakdownSeries(
  series: readonly BreakdownSeriesSubject[],
  ownColor: string,
  palette: readonly string[],
  storedColorByTermKey?: ReadonlyMap<string, string>
): ColoredBreakdownSeries[] {
  const ordered = series
    .map((entry, position) => ({ entry, position, ownership: classifyEntityOwnership(entry.facet) }))
    .sort((left, right) => OWNERSHIP_ORDER[left.ownership] - OWNERSHIP_ORDER[right.ownership] || left.position - right.position);

  // Only series belonging to a tracked side take part in the shared assignment; it declines to
  // colour anything else, which is correct there and handled below.
  const trackedSubjects = ordered
    .map((item) => item.entry)
    .filter((entry): entry is BreakdownSeriesSubject & { facet: TaxonomyFacet } => entry.facet !== undefined);

  const colorMap = buildEntityColorMap(
    trackedSubjects.map((entry) => ({ name: entry.label, type: entry.facet, terms: [entry.id] })),
    ownColor,
    palette,
    undefined,
    storedColorByTermKey
  );

  const used = Array.from(colorMap.byTermKey.values());
  let nextSpare = 0;
  const spareColor = (): string => {
    // Skip a palette slot perceptually close to a colour already assigned — a stored colour that
    // took a hue near a spare would otherwise read as the same series on the chart, not exact-string
    // equality alone.
    while (nextSpare < palette.length && used.some((assigned) => colorPerceptuallyClose(assigned, palette[nextSpare]))) {
      nextSpare += 1;
    }
    const color = palette[nextSpare] ?? palette[palette.length - 1] ?? ownColor;
    nextSpare += 1;
    return color;
  };

  return ordered.map((item) => ({
    ...item.entry,
    ownership: item.ownership,
    color: colorMap.byTermKey.get(item.entry.id) ?? spareColor(),
  }));
}
